import isClient from '../../../common/helpers/environment';
import { Logger } from '../../../common/types/loggerTypes';
import { getCountry } from '../../../common/formatting/formattingService';
import { Dictionary } from '../../../../types/globals';
import { BrazeConfig } from '../../../common/types/remoteConfig';
import { Config } from '../../../common/types/config';
import { BrazeMessageIdGenerator, IdGenerator } from './MessageIDGenerator';

export const BRAZE_CLIENT_CONNECTED_TO_STORE = 'BRAZE_CLIENT_CONNECTED_TO_STORE';
const brazeCdnUrl = 'https://js.appboycdn.com/web-sdk/2.3/appboy.min.js';

interface AppboyInitializeOptions {
    baseUrl?: string;
    minimumIntervalBetweenTriggerActionsInSeconds?: number;
    enableHtmlInAppMessages?: boolean;
}

interface InAppMessage {
    extras: Dictionary<any>;
}

interface Appboy {
    initialize: (apiKey: string, options: AppboyInitializeOptions) => void;
    changeUser: (userId: string) => void;
    subscribeToNewInAppMessages: (callback: (messages: InAppMessage[]) => InAppMessage[] | undefined) => void;
    openSession: () => void;
    logCustomEvent: (eventName: string, properties: object) => void;
    logInAppMessageImpression: (message: InAppMessage) => void;
    display: {
        showInAppMessage: (message: InAppMessage) => void;
    };
}

interface AppboyWindow {
    appboy?: Appboy;
}

enum InitizliationStatus {
    notInitilized,
    loading,
    initialized,
    failed,
}

export enum BrazeEvents {
    TRIGGER_RATE_YOUR_PRODUCT = 'TRIGGER_RATE_YOUR_PRODUCT',
}
interface ActionSubsribers {
    eventType: BrazeEvents;
    callback: Function;
}
interface CustomEventArgs {
    eventName: string;
    properties: object;
}

const loadBrazeScript = async () => {
    const brazeScript = document.createElement('script');
    brazeScript.src = brazeCdnUrl;
    await new Promise((resolve, reject) => {
        brazeScript.onload = () => {
            resolve();
        };

        brazeScript.onerror = err => {
            reject(err);
        };

        document.body.appendChild(brazeScript);
    });
};

export interface BrazeClientInterface {
    initialize: () => Promise<void>;
    logCustomEvent: (eventName: string, properties: object) => Promise<void>;
    setUser: (loginHash: string) => Promise<void>;
    logInAppMessageImpression: (messageId: string) => void;
}

type BrazeDeviceType = 'desktop' | 'mobile';

export interface BrazeClientConfig {
    device: BrazeDeviceType;
    authentication: string;
    countryCode: string;
}

interface DefaultEventProperties {
    origin: 'shop';
    device: BrazeDeviceType;
}

export const createBrazeClientConfig = (braze: BrazeConfig, config: Config, isDesktop: boolean): BrazeClientConfig => {
    return {
        authentication: isDesktop ? braze.authentication : braze.authenticationMobile,
        countryCode: config.countryCode,
        device: isDesktop ? 'desktop' : 'mobile',
    };
};

export default class BrazeClient implements BrazeClientInterface {
    private status: InitizliationStatus = InitizliationStatus.notInitilized;

    private brazeApi: Appboy;

    private brazeUserId: string | null = null;

    private apiKey: string;

    private country: string;

    private lastSentEvent: CustomEventArgs | null = null;

    private loadingScriptPromise: Promise<void>;

    private actionSubribers: ActionSubsribers[];

    private messagesMapper: Dictionary<InAppMessage>;

    private messageIdGenerator: IdGenerator;

    private defaultEventProperties: DefaultEventProperties;

    public constructor(
        private logger: Logger,
        config: BrazeClientConfig,
        private loadScript: () => Promise<void> = loadBrazeScript
    ) {
        if (!isClient()) {
            this.logger.error('BrazeClient should only be used client-side');
        }

        this.defaultEventProperties = {
            origin: 'shop',
            device: config.device,
        };

        this.apiKey = config.authentication;
        this.country = getCountry(config.countryCode);
        this.messageIdGenerator = new BrazeMessageIdGenerator();
        this.actionSubribers = [];
    }

    public async logInAppMessageImpression(messageId: string) {
        await this.initialize();
        this.brazeApi.logInAppMessageImpression(<InAppMessage>this.messagesMapper[messageId]);
        delete this.messagesMapper[messageId];
    }

    public async initialize() {
        if (this.status === InitizliationStatus.initialized || this.status === InitizliationStatus.loading) {
            await this.loadingScriptPromise;
            return;
        }

        this.status = InitizliationStatus.loading;

        try {
            this.loadingScriptPromise = this.loadScript();
            await this.loadingScriptPromise;
            const appboyWindow = window as AppboyWindow;
            if (appboyWindow.appboy) {
                this.brazeApi = appboyWindow.appboy;
                this.logger.debug('BRAZE initializing braze', { apiKey: this.apiKey });
                this.brazeApi.initialize(this.apiKey, {
                    baseUrl: 'https://sdk.api.appboy.eu/api/v3',
                    minimumIntervalBetweenTriggerActionsInSeconds: 30,
                    enableHtmlInAppMessages: true,
                });

                this.status = InitizliationStatus.initialized;
                this.messagesMapper = {};
            }
        } catch (error) {
            this.logger.error(`Braze script failed to load from ${brazeCdnUrl}`, { error: error.toString() });
            this.status = InitizliationStatus.failed;
        }
    }

    private wasEventJustSent(eventName: string, properties: object) {
        if (!this.lastSentEvent) {
            return false;
        }
        const lastEvent = this.lastSentEvent;

        if (eventName !== lastEvent.eventName) {
            return false;
        }

        if (Object.keys(properties).length !== Object.keys(lastEvent.properties).length) {
            return false;
        }

        return Object.keys(properties)
            .map(key => properties[key] === lastEvent.properties[key])
            .reduce((areAllEqual, isPairEqual) => areAllEqual && isPairEqual, true);
    }

    public async logCustomEvent(eventName: string, properties: object = {}) {
        if (this.wasEventJustSent(eventName, properties)) {
            return;
        }
        this.lastSentEvent = { eventName, properties };

        await this.initialize();
        const customData = { ...this.defaultEventProperties, ...properties };
        this.logger.debug('BRAZE sending event', { eventName, customData });
        this.brazeApi.logCustomEvent(eventName, customData);
    }

    private onMessagesReceived(messages: InAppMessage[]) {
        const currentMessage = messages[0];

        if (currentMessage.extras.isReviewsReminderOverlay) {
            const messageId = this.messageIdGenerator.id;
            this.messagesMapper[messageId] = currentMessage;
            this.triggerBrazeEvent(BrazeEvents.TRIGGER_RATE_YOUR_PRODUCT, {
                messageId,
            });
        } else {
            // a default fallback to braze api that will show an overlay based on the message data
            this.brazeApi.display.showInAppMessage(currentMessage);
        }
        // We return the rest unprocessed messages to be processed on the next tick
        return messages.slice(1);
    }

    public async setUser(loginHash: string) {
        const brazeUserId = `${this.country}_${loginHash}`;
        if (this.brazeUserId === brazeUserId) {
            return;
        }

        this.brazeUserId = brazeUserId;
        await this.initialize();

        this.logger.debug('BRAZE changeUser', { brazeUserId });
        this.brazeApi.changeUser(brazeUserId);
        this.logger.debug('BRAZE subscribeToNewInAppMessages');
        this.brazeApi.subscribeToNewInAppMessages(messages => this.onMessagesReceived(messages));
        this.logger.debug('BRAZE openning session');
        this.brazeApi.openSession();
    }

    public triggerBrazeEvent(eventType: BrazeEvents, payload?: Dictionary<any>) {
        const availableSubscriber = this.actionSubribers.find(subscriber => subscriber.eventType === eventType);
        if (availableSubscriber) {
            availableSubscriber.callback(payload);
        }
    }

    public subscribeEvent(eventType: BrazeEvents, callback: Function) {
        this.actionSubribers.push({ eventType, callback });
    }
}
